import { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
} from "@mui/material";
import { CancelScheduleSend, GroupAdd } from "@mui/icons-material";
import { TELECONSULTORIA_GUEST_ALREADY_INVITED } from "../constants";
import { useHostTeleconsultoriaInvite } from "../invites/hooks/useHostTeleconsultoriaInvite";
import type { TeleconsultoriaGuest } from "../types";
import { teleconsultoriaGuestOfAppointment } from "../utils/actors";
import { TeleconsultoriaProfessionalPicker } from "./TeleconsultoriaProfessionalPicker";

interface InviteTeleconsultoriaButtonProps {
  appointment?: fhir5.Appointment;
  /** Mantido por compatibilidade: o espelho no Encounter agora é feito no aceite. */
  encounterId?: string;
  onRoomReady: (roomUrl: string) => void;
}

/**
 * Convite de teleconsultoria pelo host.
 * Agora o convite é uma Task: o teleconsultor só entra no Appointment quando
 * aceita (garante que ele realmente participou). O host já recebe a sala.
 */
export function InviteTeleconsultoriaButton({
  appointment,
  onRoomReady,
}: InviteTeleconsultoriaButtonProps) {
  const [open, setOpen] = useState(false);
  const [guest, setGuest] = useState<TeleconsultoriaGuest | null>(null);
  const { pendingInvite, send, cancel, isSending, isCancelling } =
    useHostTeleconsultoriaInvite({ appointment, onRoomReady });

  // Só 1 teleconsultor por evolução: o já gravado (aceitou o convite).
  const currentGuest = teleconsultoriaGuestOfAppointment(appointment).practitioner;
  const hasGuest = Boolean(currentGuest);
  const blockedTooltip = currentGuest?.display
    ? `Teleconsultor já convidado: ${currentGuest.display}`
    : TELECONSULTORIA_GUEST_ALREADY_INVITED;

  const handleInvite = async () => {
    if (!appointment?.id || !guest) return;
    const isSent = await send(guest);
    if (!isSent) return;
    setOpen(false);
    setGuest(null);
  };

  // Convite pendente: o mesmo lugar do botão vira "Cancelar convite".
  if (pendingInvite && !hasGuest) {
    const invitedName = pendingInvite.for?.display || "teleconsultor";
    return (
      <Tooltip title={`Aguardando ${invitedName} aceitar o convite`}>
        <span>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<CancelScheduleSend fontSize="small" />}
            onClick={() => void cancel()}
            disabled={isCancelling}
            sx={{ textTransform: "none", borderRadius: 2, minHeight: 38 }}
          >
            {isCancelling ? "Cancelando..." : "Cancelar convite"}
          </Button>
        </span>
      </Tooltip>
    );
  }

  return (
    <>
      {/* span: o Tooltip precisa de um wrapper quando o botão está desabilitado. */}
      <Tooltip title={hasGuest ? blockedTooltip : ""}>
        <span>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<GroupAdd fontSize="small" />}
            onClick={() => setOpen(true)}
            disabled={hasGuest}
            sx={{ textTransform: "none", borderRadius: 2, minHeight: 38 }}
          >
           Teleconsultoria
          </Button>
        </span>
      </Tooltip>
      {open && (
        <Dialog
          open={open}
          onClose={() => {
            if (!isSending) setOpen(false);
          }}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Selecionar teleconsultor</DialogTitle>
          <DialogContent dividers>
            <TeleconsultoriaProfessionalPicker
              selected={guest}
              onSelect={setGuest}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setOpen(false)}
              disabled={isSending}
              sx={{ textTransform: "none" }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={() => void handleInvite()}
              disabled={!guest || isSending}
              sx={{ textTransform: "none" }}
            >
              {isSending ? "Enviando..." : "Enviar convite"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
